import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@components/ui/button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/shadcn/dialog';
import { Input } from '@components/ui/shadcn/input';
import { Label } from '@components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/shadcn/select';
import { TextArea } from '@components/ui/textarea/TextArea';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryModel } from '../../models/Country';
import { CoverageRuleModel, CreateCoverageRulePayload } from '../../models/CoverageRule';
import { PaymentMethodModel } from '../../models/PaymentMethod';

interface CoverageRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateCoverageRulePayload) => void;
  rule?: CoverageRuleModel | null;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
  paymentMethods: PaymentMethodModel[];
  countries: CountryModel[];
}

export const CoverageRuleModal = ({
  isOpen,
  onClose,
  onSubmit,
  rule,
  isSubmitting = false,
  mode,
  paymentMethods,
  countries,
}: CoverageRuleModalProps) => {
  const { t } = useTranslation('coverageZonesModule');

  // Form state
  const [countryId, setCountryId] = useState<string>('');
  const [cpFrom, setCpFrom] = useState<string>('');
  const [cpTo, setCpTo] = useState<string>('');
  const [ruleType, setRuleType] = useState<'allow' | 'deny'>('allow');
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with rule data in edit mode
  useEffect(() => {
    if (mode === 'edit' && rule) {
      setCountryId(rule.country_id.toString());
      setCpFrom(rule.cp_from || '');
      setCpTo(rule.cp_to || '');
      setRuleType(rule.rule);
      setPaymentMethodId(rule.payment_method_id?.toString() || '');
      setNotes(rule.notes || '');
    } else {
      // Reset form for create mode
      setCountryId('');
      setCpFrom('');
      setCpTo('');
      setRuleType('allow');
      setPaymentMethodId('');
      setNotes('');
    }
    setErrors({});
  }, [mode, rule, isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!countryId) {
      newErrors.country_id = t('validation.countryRequired');
    }

    if (!notes || notes.trim() === '') {
      newErrors.notes = t('validation.notesRequired');
    }

    // Validar formato de códigos postales
    const cpRegex = /^[0-9]{4,6}$/;
    if (cpFrom && !cpRegex.test(cpFrom)) {
      newErrors.cp_from = t('validation.invalidCpFormat');
    }
    if (cpTo && !cpRegex.test(cpTo)) {
      newErrors.cp_to = t('validation.invalidCpFormat');
    }

    // Validar rango
    if (cpFrom && cpTo && cpFrom > cpTo) {
      newErrors.cp_to = t('validation.cpToGreaterThanFrom');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const payload: CreateCoverageRulePayload = {
      country_id: parseInt(countryId),
      cp_from: cpFrom || null,
      cp_to: cpTo || null,
      payment_method_id: paymentMethodId ? parseInt(paymentMethodId) : null,
      notes: notes.trim(), // Obligatorio, quitar espacios
      type: ruleType,
    };

    onSubmit(payload);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto transition-colors">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {mode === 'create' ? t('coverageRules.addRule') : t('coverageRules.editRule')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {mode === 'create'
              ? 'Completa los datos para crear una nueva regla de cobertura'
              : 'Modifica los datos de la regla de cobertura'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* País */}
          <div className="space-y-2">
            <Label htmlFor="country_id" className="text-foreground">
              {t('form.country')} <span className="text-destructive">*</span>
            </Label>
            <Combobox
              options={countries.map((c) => ({
                label: c.name,
                value: String(c.id),
                // mejora de búsqueda: nombre + códigos
                searchValue: `${c.name} ${c.iso2 || ''} ${c.iso3 || ''}`.trim(),
              }))}
              placeholder={t('form.selectCountry')}
              value={countryId}
              setValue={setCountryId}
              disabled={isSubmitting}
            />
            {errors.country_id && <p className="text-sm text-destructive">{errors.country_id}</p>}
          </div>

          {/* Código Postal Desde */}
          <div className="space-y-2">
            <Label htmlFor="cp_from" className="text-foreground">
              {t('form.cpFrom')} <span className="text-muted-foreground text-xs">({t('form.optional')})</span>
            </Label>
            <Input
              id="cp_from"
              type="text"
              value={cpFrom}
              onChange={(e) => setCpFrom(e.target.value)}
              placeholder="01000"
              disabled={isSubmitting}
              className={errors.cp_from ? 'border-destructive' : ''}
            />
            {errors.cp_from && <p className="text-sm text-destructive">{errors.cp_from}</p>}
          </div>

          {/* Código Postal Hasta */}
          <div className="space-y-2">
            <Label htmlFor="cp_to" className="text-foreground">
              {t('form.cpTo')} <span className="text-muted-foreground text-xs">({t('form.optional')})</span>
            </Label>
            <Input
              id="cp_to"
              type="text"
              value={cpTo}
              onChange={(e) => setCpTo(e.target.value)}
              placeholder="01999"
              disabled={isSubmitting}
              className={errors.cp_to ? 'border-destructive' : ''}
            />
            {errors.cp_to && <p className="text-sm text-destructive">{errors.cp_to}</p>}
          </div>

          {/* Tipo de Regla */}
          <div className="space-y-2">
            <Label htmlFor="rule_type" className="text-foreground">
              {t('form.type')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={ruleType}
              onValueChange={(value) => setRuleType(value as 'allow' | 'deny')}
              disabled={isSubmitting}
            >
              <SelectTrigger id="rule_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">{t('form.typeAllow')}</SelectItem>
                <SelectItem value="deny">{t('form.typeDeny')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Método de Pago */}
          <div className="space-y-2">
            <Label htmlFor="payment_method" className="text-foreground">
              {t('form.paymentMethod')} <span className="text-muted-foreground text-xs">({t('form.optional')})</span>
            </Label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Select value={paymentMethodId} onValueChange={setPaymentMethodId} disabled={isSubmitting}>
                  <SelectTrigger id="payment_method">
                    <SelectValue placeholder={t('form.selectPaymentMethod')} />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.id.toString()}>
                        {method.method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {paymentMethodId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setPaymentMethodId('');
                  }}
                  disabled={isSubmitting}
                  className="px-3 shrink-0"
                >
                  Limpiar
                </Button>
              )}
            </div>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-foreground">
              {t('form.notes')} <span className="text-destructive">*</span>
            </Label>
            <TextArea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Información adicional sobre esta regla..."
              rows={3}
              disabled={isSubmitting}
              className={`resize-none ${errors.notes ? 'border-destructive' : ''}`}
            />
            {errors.notes && <p className="text-sm text-destructive">{errors.notes}</p>}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="transition-colors"
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} className="transition-colors">
              {isSubmitting ? 'Guardando...' : mode === 'create' ? 'Crear Regla' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
